import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';
import { istNetzFehler } from '../netzFehler';
import type {
  FreigabePublicErgebnis,
  FreigabeUnterschriftErgebnis,
  GbuFassungInhalt,
  GbuUnterschrift,
  UnterschriftRolle,
} from './gbuTypes';

/**
 * Die ANONYME Freigabeseite `#/gbu-freigabe/<token>` — nur RPCs, keine
 * Tabelle (docs/sifa-gbu-plan.md §8, §14.2). `anon` hat auf keiner
 * `sifa_gbu*`-Tabelle Rechte; alles laeuft ueber die security-definer-
 * Funktionen aus supabase/sifa_gbu_rpc.sql (Nr. 168), die den Token pruefen
 * (Vorbild report_freimeldung_rpc.sql / freimeldungPublicService.ts).
 *
 * RPC-Namen und Parameter EXAKT wie in Nr. 168. Ein umbenannter Parameter
 * bricht die Seite still: die Datenbank antwortet mit PGRST202 — das wird
 * hier zu `fehler: 'schema'`.
 *
 * WIRFT NIE. Die RPCs melden Fehlstatus als `{ ok: false, fehler }`; dieses
 * Modul bildet sie auf die Typen aus gbuTypes.ts ab. Kein Netz → 'netz';
 * jeder andere Serverfehler ebenfalls → 'netz' („bitte spaeter erneut") —
 * ein anonymer Besucher bekommt keine Diagnose, die Konsole schon.
 */

type Zeile = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const strOrNull = (v: unknown): string | null => {
  if (v == null) return null;
  const s = str(v).trim();
  return s ? s : null;
};

/** Erster gesetzter Wert unter mehreren Schluesseln — die RPC darf snake ODER camel liefern. */
const pick = (row: Zeile, ...keys: string[]): unknown => {
  for (const k of keys) if (row?.[k] !== undefined && row?.[k] !== null) return row[k];
  return undefined;
};

/** jsonb-Antwort einer RPC als Objekt; Zeichenketten werden geparst, ein Array liefert seine erste Zeile. */
const alsObjekt = (data: unknown): Zeile | null => {
  if (data == null) return null;
  if (Array.isArray(data)) return data.length > 0 ? alsObjekt(data[0]) : null;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Zeile) : null;
    } catch {
      return null;
    }
  }
  return typeof data === 'object' ? (data as Zeile) : null;
};

const errorCode = (e: unknown): string =>
  typeof e === 'object' && e !== null ? str((e as { code?: unknown }).code) : '';

/** Migration Nr. 168 noch nicht gefahren: PostgREST kennt die Funktion nicht. */
const istFehlendeFunktion = (e: unknown): boolean => {
  const code = errorCode(e);
  const msg = typeof e === 'object' && e !== null ? str((e as { message?: unknown }).message) : '';
  return code === 'PGRST202' || code === '42883' || /could not find the function/i.test(msg);
};

const ROLLEN: readonly UnterschriftRolle[] = ['erstellt', 'geprueft', 'freigegeben'];

/** Groesste Unterschrift (PNG-DataURL) in Zeichen — gleich der Pruefung der RPC. */
export const FREIGABE_UNTERSCHRIFT_MAX_ZEICHEN = 150000;
/** Laengster Name — gleich der Pruefung der RPC. */
export const FREIGABE_NAME_MAX = 120;

/** Token-Laenge, die die RPCs ueberhaupt nachschlagen (32–128); alles andere ist 'unbekannt' ohne Aufruf. */
const tokenPasst = (t: string): boolean => t.length >= 32 && t.length <= 128;

const WEGE: ReadonlyArray<GbuUnterschrift['weg']> = ['geraet', 'link', 'papier'];

/**
 * Die bisherigen Unterschriften der Fassung — die RPC liefert sie unter
 * einem EIGENEN Schluessel `unterschriften`, nicht im Inhalt. Geprueft
 * durchgereicht: keine Liste (auch eine kaputte Zeichenkette) → []; ein
 * Eintrag ohne bekannte Rolle, ohne Namen oder mit unbekanntem Weg faellt
 * heraus; ein Bild, das keine `data:image/`-Adresse ist oder die Grenze der
 * RPC sprengt, wird null — es landet sonst ungeprueft in einem <img src>.
 */
export const mapUnterschriften = (v: unknown): GbuUnterschrift[] => {
  let liste: unknown = v;
  if (typeof v === 'string') {
    try {
      liste = JSON.parse(v);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(liste)) return [];
  const aus: GbuUnterschrift[] = [];
  for (const roh of liste) {
    const u = roh && typeof roh === 'object' && !Array.isArray(roh) ? (roh as Zeile) : null;
    if (!u) continue;
    const rolle = str(u.rolle);
    const name = str(u.name).trim();
    const weg = str(u.weg);
    if (!(ROLLEN as readonly string[]).includes(rolle) || !name || !(WEGE as readonly string[]).includes(weg)) continue;
    const bild = typeof u.bild === 'string' && u.bild.startsWith('data:image/') && u.bild.length <= FREIGABE_UNTERSCHRIFT_MAX_ZEICHEN
      ? u.bild
      : null;
    aus.push({
      rolle: rolle as UnterschriftRolle,
      name: name.slice(0, FREIGABE_NAME_MAX),
      funktion: strOrNull(u.funktion),
      datum: str(u.datum),
      bild,
      weg: weg as GbuUnterschrift['weg'],
    });
  }
  return aus;
};

type FreigabeFehler = Extract<FreigabePublicErgebnis, { ok: false }>;
type UnterschriftFehler = Extract<FreigabeUnterschriftErgebnis, { ok: false }>;

/** Ein unerwarteter Fehler: kein Netz, ein fehlendes Schema oder etwas anderes — die Konsole bekommt die Diagnose. */
const fehlerArt = (e: unknown): 'schema' | 'netz' => {
  if (istFehlendeFunktion(e)) return 'schema';
  if (!istNetzFehler(e)) console.warn('GBU-Freigabe: unerwarteter Serverfehler', describeError(e));
  return 'netz';
};

// ─── Lesen ──────────────────────────────────────────────────────────────────

/**
 * Was zum Freigabe-Link gehoert (RPC `sifa_gbu_freigabe_public`): Rolle,
 * vorbelegter Name, Ablauf, ob der Link schon benutzt wurde, der
 * eingefrorene Inhalt der Fassung — die Seite baut daraus das PDF — und die
 * bisherigen Unterschriften (eigener Schluessel, mapUnterschriften).
 *
 * `fehler`: 'unbekannt' (kein solcher Link, auch: Fassung zurueckgezogen),
 * 'abgelaufen', 'schema' (Nr. 168 fehlt), 'netz'. Ein Link ohne lesbaren
 * Inhalt gilt als 'unbekannt' — eine leere Seite zum Unterschreiben waere
 * schlimmer als eine Absage.
 */
export const ladeFreigabe = async (token: string): Promise<FreigabePublicErgebnis> => {
  const t = (token || '').trim();
  if (!tokenPasst(t)) return { ok: false, fehler: 'unbekannt' };
  try {
    const { data, error } = await supabase.rpc('sifa_gbu_freigabe_public', { p_token: t });
    if (error) return { ok: false, fehler: fehlerArt(error) };
    const row = alsObjekt(data);
    if (!row) return { ok: false, fehler: 'unbekannt' };
    if (row.ok !== true) {
      const f = str(row.fehler);
      return { ok: false, fehler: f === 'abgelaufen' ? 'abgelaufen' : 'unbekannt' } as FreigabeFehler;
    }
    const inhalt = alsObjekt(row.inhalt);
    const rolle = str(row.rolle);
    if (!inhalt || !(ROLLEN as readonly string[]).includes(rolle)) return { ok: false, fehler: 'unbekannt' };
    return {
      ok: true,
      rolle: rolle as UnterschriftRolle,
      nameVorgabe: strOrNull(pick(row, 'name_vorgabe', 'nameVorgabe')),
      gueltigBis: str(pick(row, 'gueltig_bis', 'gueltigBis')),
      bereits: row.bereits === true,
      inhalt: inhalt as unknown as GbuFassungInhalt,
      unterschriften: mapUnterschriften(row.unterschriften),
    };
  } catch (e) {
    return { ok: false, fehler: fehlerArt(e) };
  }
};

// ─── Unterschreiben ─────────────────────────────────────────────────────────

/**
 * Unterschreibt per Link (RPC `sifa_gbu_freigabe_unterschreiben`). Bei der
 * Rolle „freigegeben" gibt die Datenbank die Fassung damit frei (die RPC
 * meldet das als `freigegeben: true`; der Typ in gbuTypes.ts traegt das Feld
 * nicht — die Seite kennt die Rolle aus ladeFreigabe). Ein schon benutzter
 * Link ist `{ ok: true, bereits: true }` — der zweite Klick ist der
 * Normalfall, nicht die Ausnahme.
 *
 * Name (1–120 Zeichen) und Bild (`data:image/…`, hoechstens 150 000 Zeichen)
 * werden VOR dem Aufruf geprueft, mit denselben Grenzen wie die RPC — eine
 * Absage kommt dann als `fehler: 'eingabe'` mit `text`.
 */
export const unterschreibeFreigabe = async (
  token: string,
  name: string,
  bildDataUrl: string,
): Promise<FreigabeUnterschriftErgebnis> => {
  const t = (token || '').trim();
  if (!tokenPasst(t)) return { ok: false, fehler: 'unbekannt' };
  const n = (name || '').replace(/\s+/g, ' ').trim();
  if (!n) return { ok: false, fehler: 'eingabe', text: 'Bitte den Namen eintragen.' };
  if (n.length > FREIGABE_NAME_MAX) return { ok: false, fehler: 'eingabe', text: `Der Name ist zu lang (höchstens ${FREIGABE_NAME_MAX} Zeichen).` };
  const bild = (bildDataUrl || '').trim();
  if (!bild.startsWith('data:image/')) return { ok: false, fehler: 'eingabe', text: 'Bitte zuerst unterschreiben.' };
  if (bild.length > FREIGABE_UNTERSCHRIFT_MAX_ZEICHEN) {
    return { ok: false, fehler: 'eingabe', text: 'Die Unterschrift ist zu groß — bitte löschen und kleiner erneut unterschreiben.' };
  }

  try {
    const { data, error } = await supabase.rpc('sifa_gbu_freigabe_unterschreiben', {
      p_token: t,
      p_name: n,
      p_unterschrift: bild,
    });
    if (error) return { ok: false, fehler: fehlerArt(error), text: describeError(error) };
    const row = alsObjekt(data);
    if (!row) return { ok: false, fehler: 'netz', text: 'Die Freigabe wurde nicht bestätigt — bitte erneut versuchen.' };
    if (row.ok === true) return { ok: true, bereits: row.bereits === true };
    const f = str(row.fehler);
    const fehler: UnterschriftFehler['fehler'] = f === 'abgelaufen' || f === 'eingabe' ? f : 'unbekannt';
    const text = strOrNull(pick(row, 'text', 'meldung'));
    return text ? { ok: false, fehler, text } : { ok: false, fehler };
  } catch (e) {
    return { ok: false, fehler: fehlerArt(e), text: describeError(e) };
  }
};
